import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetLoggedInUserDetails } from "../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { PendingBillsSummary } from "../../../../api/services/reports/models";
import { ReactComponent as ExportIcon } from "../../../../assets/images/export.svg";
import {
  Column,
  FormController,
  MuiBox,
  MuiButtonTertiary,
  MuiDateField,
  MuiLink,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  RowProps,
} from "../../../../components";
import MuiChipWithPopover from "../../../../components/chipWithPopover/MuiChipWithPopover";
import { StatusBadge } from "../../../../components/statusBadge/StatusBadge";
import {
  EMPTY_VALUE_DASH,
  MAX_LENGTH_50,
  PAGE_IDS,
  PAGE_URLS,
} from "../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../helpers/constants/countryDateFormat";
import {
  filterListByFilterKeyValue,
  getAmountWithCurrency,
  isNotEmpty,
} from "../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { isRecordTypeProgram, mapPendingBillingReports } from "../../helper";
import {
  PendingBillsSummaryRow,
  pendingBillingListColumnsKey,
} from "../../modelsAndSchemas/models";
import { reportsResultsSiteCoreKeys } from "../../siteCore/SiteCoreKeys";

const pageId = `${PAGE_IDS.REPORTS}-list`;

const {
  key,
  clientName,
  type,
  status,
  participantName,
  localClientRelationshipOwnerName,
  fees,
  totalTaxInvoiced,
  totalExpenseInvoiced,
  totalFeesInvoiced,
  totalOutstandingAmount,
  billingContact,
  poNumbers,
  productLineName,
  programTemplateName,
  alternateProgramTemplateName,
  hrContact,
  costCenter,
  businessUnit,
  deliveryOffice,
  nextView,
  actualStartDate,
} = pendingBillingListColumnsKey;

interface PendingBillingReportsListProps {
  pendingBillings: PendingBillsSummary[];
  isFilteredPendingBillings: boolean;
  onExport: () => void;
}
export const PendingBillingReportsList = ({
  pendingBillings,
  isFilteredPendingBillings = false,
  onExport,
}: PendingBillingReportsListProps) => {
  const {
    commonLabels,
    useFetchLabel,
    fetchValueByKeyOfDropdown,
    fetchDropdownValues,
    fetchComponentTitle,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [state, setState] = useState<{
    filteredReports: PendingBillsSummaryRow[];
  }>({
    filteredReports: [],
  });

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const formattedPendingBillings = useMemo(() => {
    if (!pendingBillings?.length) return [];
    return mapPendingBillingReports(pendingBillings, userCountryDateFormat);
  }, [pendingBillings, userCountryDateFormat]);

  const { control, setValue, getValues } = useForm({
    defaultValues: {
      [key]: "",
      [type]: "",
      [clientName]: "",
      [status]: "",
      [participantName]: "",
      [localClientRelationshipOwnerName]: "",
      [billingContact]: "",
      [poNumbers]: "",
      [productLineName]: "",
      [programTemplateName]: "",
      [alternateProgramTemplateName]: "",
      [hrContact]: "",
      [costCenter]: "",
      [businessUnit]: "",
      [deliveryOffice]: "",
      [nextView]: "",
      [actualStartDate]: "",
    },
  });

  //   watch entire form state
  const filters = useWatch({ control });

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  useEffect(() => {
    appliedFilters();
  }, [filters]);

  const appliedFilters = () => {
    if (!formattedPendingBillings.length) return;

    const filterList = filterListByFilterKeyValue(formattedPendingBillings, {
      ...filters,
      actualStartDate: filters.actualStartDate,
    }) as PendingBillsSummaryRow[];

    setState((prevState) => ({
      ...prevState,
      filteredReports: filterList,
    }));
    resetPagination();
  };

  useEffect(() => {
    if (formattedPendingBillings) {
      setState((prevState) => ({
        ...prevState,
        filteredReports: formattedPendingBillings,
      }));
    }
  }, [formattedPendingBillings]);

  const tableRef = useRef<{ resetPagination: () => void }>(null);

  const getLabel = useFetchLabel(reportsResultsSiteCoreKeys.header);

  const statusOptions = fetchDropdownValues(
    reportsResultsSiteCoreKeys.header,
    reportsResultsSiteCoreKeys.programStatusList,
  );

  const getHrefForNavigation = (programProjectId: string, type: string) => {
    if (isRecordTypeProgram(type)) {
      return `${PAGE_URLS.VIEW_PROGRAM}/${programProjectId}`;
    } else {
      return `${PAGE_URLS.VIEW_PROJECT}/${programProjectId}`;
    }
  };

  const columns: Column[] = [
    {
      id: key,
      label: getLabel(reportsResultsSiteCoreKeys.programKey),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiLink
          href={getHrefForNavigation(row["id"], row["type"])}
          isOpenNewTab
        >
          <MuiBox>
            <MuiTypography variant="h3" className="Link">
              {row[key]}
            </MuiTypography>
          </MuiBox>
        </MuiLink>
      ),
    },
    {
      id: type,
      label: getLabel(reportsResultsSiteCoreKeys.type),
      minWidth: 200,
      isSortVisible: true,
      render: (row: RowProps) => {
        return fetchValueByKeyOfDropdown(
          reportsResultsSiteCoreKeys.header,
          reportsResultsSiteCoreKeys.typeList,
          row?.type,
        );
      },
    },
    {
      id: clientName,
      label: getLabel(reportsResultsSiteCoreKeys.clientName),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiLink
          href={`${PAGE_URLS.VIEW_CLIENT}/${row["clientId"]}`}
          isOpenNewTab
        >
          <MuiBox>
            <MuiTypography variant="h3" className="Link">
              {row[clientName]}
            </MuiTypography>
          </MuiBox>
        </MuiLink>
      ),
    },

    {
      id: status,
      label: getLabel(reportsResultsSiteCoreKeys.programStatusDropDown),
      isSortVisible: true,
      render: (row: RowProps) => {
        const label = fetchValueByKeyOfDropdown(
          reportsResultsSiteCoreKeys.header,
          reportsResultsSiteCoreKeys.programStatusList,
          row?.status,
        );
        return <StatusBadge status={row?.status} label={label} />;
      },
    },
    {
      id: participantName,
      label: getLabel(reportsResultsSiteCoreKeys.participantName),
      isSortVisible: true,
    },
    {
      id: localClientRelationshipOwnerName,
      label: getLabel(reportsResultsSiteCoreKeys.localClientRelationshipOwner),
      isSortVisible: true,
    },
    {
      id: fees,
      label: getLabel(reportsResultsSiteCoreKeys.totalProgramFee),
      isSortVisible: true,
      render: (row: RowProps) => {
        return getAmountWithCurrency(row[fees], row["currency"]);
      },
    },
    {
      id: totalTaxInvoiced,
      label: getLabel(reportsResultsSiteCoreKeys.totalTaxInvoicedTillDate),
      isSortVisible: true,
      render: (row: RowProps) => {
        return getAmountWithCurrency(row[totalTaxInvoiced], row["currency"]);
      },
    },
    {
      id: totalExpenseInvoiced,
      label: getLabel(reportsResultsSiteCoreKeys.totalExpensesInvoicedTillDate),
      isSortVisible: true,
      render: (row: RowProps) => {
        return getAmountWithCurrency(
          row[totalExpenseInvoiced],
          row["currency"],
        );
      },
    },
    {
      id: totalFeesInvoiced,
      label: getLabel(reportsResultsSiteCoreKeys.totalFeesInvoicedTillDate),
      isSortVisible: true,
      render: (row: RowProps) => {
        return getAmountWithCurrency(row[totalFeesInvoiced], row["currency"]);
      },
    },
    {
      id: totalOutstandingAmount,
      label: getLabel(reportsResultsSiteCoreKeys.outstandingAmount),
      isSortVisible: true,
      render: (row: RowProps) => {
        return getAmountWithCurrency(
          row[totalOutstandingAmount],
          row["currency"],
        );
      },
    },

    {
      id: hrContact,
      label: getLabel(reportsResultsSiteCoreKeys.hrContact),
      isSortVisible: true,
    },
    {
      id: billingContact,
      label: getLabel(reportsResultsSiteCoreKeys.billingContact),
      isSortVisible: true,
    },
    {
      id: poNumbers,
      label: getLabel(reportsResultsSiteCoreKeys.poNumber),
      isSortVisible: true,
      render: (row: any) =>
        isNotEmpty(row.poNumbers) ? (
          <MuiChipWithPopover
            isEditable={false}
            data={row.poNumbers.map((poNumber: string) => ({
              label: poNumber,
              value: poNumber,
            }))}
            headerLabel={getLabel(reportsResultsSiteCoreKeys.poNumber)}
            gridRef={React.createRef<HTMLElement>()}
            removeChipNameARIALabel={commonARIALabel.removeChipName}
            showMoreARIALabel={commonARIALabel.showNMore}
            closePopoverARIALabel={commonARIALabel.closePopover}
          />
        ) : (
          EMPTY_VALUE_DASH
        ),
    },
    {
      id: costCenter,
      label: getLabel(reportsResultsSiteCoreKeys.costCenter),
      isSortVisible: true,
    },
    {
      id: businessUnit,
      label: getLabel(reportsResultsSiteCoreKeys.businessUnit),
      isSortVisible: true,
    },
    {
      id: deliveryOffice,
      label: getLabel(reportsResultsSiteCoreKeys.deliveryOffice),
      isSortVisible: true,
    },
    {
      id: productLineName,
      label: getLabel(reportsResultsSiteCoreKeys.productLine),
      isSortVisible: true,
    },
    {
      id: nextView,
      label: getLabel(reportsResultsSiteCoreKeys.nextView),
      isSortVisible: true,
    },
    {
      id: programTemplateName,
      label: getLabel(reportsResultsSiteCoreKeys.programTemplate),
      isSortVisible: true,
    },
    {
      id: alternateProgramTemplateName,
      label: getLabel(reportsResultsSiteCoreKeys.alternateProgramTemplateName),
      isSortVisible: true,
    },
    {
      id: actualStartDate,
      label: getLabel(reportsResultsSiteCoreKeys.actualStartDate),
      isSortVisible: true,
    },
  ];

  const renderSearchIcon = () => (
    <InputAdornment position="start">
      <SearchIcon />
    </InputAdornment>
  );

  const tableHeaderForm: React.JSX.Element[] = [
    <FormController control={control} name={key} key={key}>
      <MuiTextField
        id={`${pageId}-${key}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController control={control} name={type} key={type}>
      <MuiSelect
        label=""
        visibleDefaultSelectOption
        options={fetchDropdownValues(
          reportsResultsSiteCoreKeys.header,
          reportsResultsSiteCoreKeys.typeList,
        )}
        value=""
        id={`${pageId}-${type}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController control={control} name={clientName} key={clientName}>
      <MuiTextField
        id={`${pageId}-${clientName}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController control={control} name={status} key={status}>
      <MuiSelect
        label=""
        options={statusOptions}
        visibleDefaultSelectOption={true}
        id={`${pageId}-${status}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={participantName}
      key={participantName}
    >
      <MuiTextField
        id={`${pageId}-${participantName}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={localClientRelationshipOwnerName}
      key={localClientRelationshipOwnerName}
    >
      <MuiTextField
        id={`${pageId}-${localClientRelationshipOwnerName}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <></>,
    <></>,
    <></>,
    <></>,
    <></>,
    <FormController control={control} name={hrContact} key={hrContact}>
      <MuiTextField
        id={`${pageId}-${hrContact}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={billingContact}
      key={billingContact}
    >
      <MuiTextField
        id={`${pageId}-${billingContact}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController control={control} name={poNumbers} key={poNumbers}>
      <MuiTextField
        id={`${pageId}-${poNumbers}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController control={control} name={costCenter} key={costCenter}>
      <MuiTextField
        id={`${pageId}-${costCenter}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController control={control} name={businessUnit} key={businessUnit}>
      <MuiTextField
        id={`${pageId}-${businessUnit}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={deliveryOffice}
      key={deliveryOffice}
    >
      <MuiTextField
        id={`${pageId}-${deliveryOffice}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={productLineName}
      key={productLineName}
    >
      <MuiTextField
        id={`${pageId}-${productLineName}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController control={control} name={nextView} key={nextView}>
      <MuiTextField
        id={`${pageId}-${nextView}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={programTemplateName}
      key={programTemplateName}
    >
      <MuiTextField
        id={`${pageId}-${programTemplateName}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={alternateProgramTemplateName}
      key={alternateProgramTemplateName}
    >
      <MuiTextField
        id={`${pageId}-${alternateProgramTemplateName}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={actualStartDate}
      key={actualStartDate}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          value={
            getValues().actualStartDate !== ""
              ? dayjs(getValues().actualStartDate, userCountryDateFormat)
              : null
          }
          format={userCountryDateFormat}
          clearable
          onChange={(newValue) => {
            setValue(
              actualStartDate,
              newValue?.format(userCountryDateFormat) ?? "",
            );
          }}
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
  ];

  const tableHeaderLabel = isFilteredPendingBillings
    ? fetchComponentTitle(reportsResultsSiteCoreKeys.header)
    : getLabel(reportsResultsSiteCoreKeys.allProgramsHeader);

  return (
    <MuiBox className="result-container">
      <MuiBox className="result-header">
        <MuiTypography variant="h2SemiBold" className="header-text">
          {tableHeaderLabel}
        </MuiTypography>
        {pendingBillings.length > 0 && (
          <MuiTypography variant="h3SemiBold" className="header-count">
            {pendingBillings.length}
          </MuiTypography>
        )}
        <MuiBox textAlign="right" flexGrow={1}>
          <MuiButtonTertiary onClick={onExport}>
            <ExportIcon />
            {commonLabels.export}
          </MuiButtonTertiary>
        </MuiBox>
      </MuiBox>

      <MuiTable
        ref={tableRef}
        pageId={pageId}
        tableHeaderForm={tableHeaderForm}
        columns={columns}
        emptyMessage={commonLabels.noRecordsFound}
        tableResults={
          state.filteredReports as unknown as Record<string, ReactNode>[]
        }
        noDataInApi={pendingBillings.length === 0}
        noResultMessage={commonLabels.searchCriteria}
        priorityResults={[]}
        isShowDashIfValueEmpty
      />
    </MuiBox>
  );
};
